import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Edit3, Eye, FileUp, Plus, Save, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankDeck = () => ({ title: "", description: "", source_type: "pdf", file_path: "", external_url: "", password: "", is_active: true });

export function AdminVault({ authHeaders }) {
  const [decks, setDecks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/decks`, { headers: authHeaders });
      setDecks(res.data || []);
    } catch { toast.error("Could not load decks"); }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setEditing((c) => ({ ...c, [k]: v }));

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/upload-deck`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
      set("file_path", res.data.file_path);
      toast.success(`Uploaded ${res.data.original_filename}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Deck needs a title"); return; }
    if (editing.source_type === "pdf" && !editing.file_path && !editing.id) { toast.error("Upload a PDF first"); return; }
    if (editing.source_type === "link" && !editing.external_url.trim()) { toast.error("Add the external link"); return; }
    setBusy(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/decks/${editing.id}`, editing, { headers: authHeaders });
        toast.success("Deck updated");
      } else {
        await axios.post(`${API}/admin/decks`, editing, { headers: authHeaders });
        toast.success("Deck created");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save deck");
    } finally { setBusy(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}" and its access logs?`)) return;
    try { await axios.delete(`${API}/admin/decks/${d.id}`, { headers: authHeaders }); toast.success("Deck deleted"); await load(); }
    catch { toast.error("Could not delete deck"); }
  };

  const viewLogs = async (d) => {
    setLogsFor(d); setLogs([]);
    try { const res = await axios.get(`${API}/admin/decks/${d.id}/access-logs`, { headers: authHeaders }); setLogs(res.data || []); }
    catch { toast.error("Could not load access logs"); }
  };

  return (
    <section className="admin-panel wide" data-testid="admin-vault-panel">
      <div className="enquiry-header">
        <h2>Pitch deck vault</h2>
        <Button className="primary-cta save-app" onClick={() => setEditing(blankDeck())} data-testid="admin-deck-new-button"><Plus size={16} /> New deck</Button>
      </div>

      <div className="admin-survey-list" data-testid="admin-deck-list">
        {decks.map((d) => (
          <article key={d.id} className="admin-survey-row" data-testid={`admin-deck-row-${d.id}`}>
            <div className="admin-survey-info">
              <strong>{d.title}</strong>
              <span className={`survey-type-badge type-${d.source_type === "link" ? "link" : "html"}`}>{d.source_type}</span>
              <span className={`survey-active-chip ${d.is_active ? "on" : "off"}`}>{d.is_active ? "Active" : "Hidden"}</span>
              {d.has_password && <span className="survey-active-chip on">Password</span>}
              <em data-testid={`admin-deck-views-${d.id}`}>{d.view_count || 0} views · {d.access_count || 0} logs</em>
            </div>
            <div className="admin-survey-actions">
              <button className="status-btn success" onClick={() => viewLogs(d)} data-testid={`admin-deck-logs-${d.id}`}><Eye size={13} /> Access logs</button>
              <button className="status-btn good" onClick={() => setEditing({ ...d, password: "", external_url: d.external_url || "", file_path: d.file_path || "" })} data-testid={`admin-deck-edit-${d.id}`}><Edit3 size={13} /> Edit</button>
              <button className="status-btn delete" onClick={() => remove(d)} data-testid={`admin-deck-delete-${d.id}`}><Trash2 size={13} /> Delete</button>
            </div>
          </article>
        ))}
        {!decks.length && <p data-testid="admin-no-decks">No decks yet.</p>}
      </div>

      {editing && (
        <div className="survey-modal-overlay" onClick={() => setEditing(null)} data-testid="admin-deck-editor-overlay">
          <div className="survey-modal admin-survey-editor" onClick={(e) => e.stopPropagation()} data-testid="admin-deck-editor">
            <div className="survey-modal-bar">
              <strong>{editing.id ? "Edit deck" : "New deck"}</strong>
              <button onClick={() => setEditing(null)} data-testid="admin-deck-editor-close"><X size={18} /></button>
            </div>
            <div className="admin-survey-editor-body">
              <label>Title</label>
              <Input value={editing.title} onChange={(e) => set("title", e.target.value)} data-testid="admin-deck-title-input" />
              <label>Description</label>
              <Textarea value={editing.description} onChange={(e) => set("description", e.target.value)} data-testid="admin-deck-description-input" />
              <label>Source</label>
              <select value={editing.source_type} onChange={(e) => set("source_type", e.target.value)} data-testid="admin-deck-source-select">
                <option value="pdf">PDF upload</option>
                <option value="link">External link</option>
              </select>

              {editing.source_type === "pdf" && (
                <div className="admin-deck-upload">
                  <input ref={fileRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => uploadPdf(e.target.files?.[0])} data-testid="admin-deck-file-input" />
                  <Button className="ghost-cta-dark" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="admin-deck-upload-button"><FileUp size={15} /> {uploading ? "Uploading…" : "Upload PDF"}</Button>
                  {editing.file_path && <span className="admin-deck-file-ok" data-testid="admin-deck-file-ok">PDF attached ✓</span>}
                </div>
              )}
              {editing.source_type === "link" && (
                <>
                  <label>External link (Google Slides, DocSend, PDF URL)</label>
                  <Input value={editing.external_url} onChange={(e) => set("external_url", e.target.value)} placeholder="https://…" data-testid="admin-deck-external-url-input" />
                </>
              )}

              <label>Password {editing.id ? "(leave blank to keep, type to change)" : "(optional)"}</label>
              <Input type="text" value={editing.password} onChange={(e) => set("password", e.target.value)} placeholder="Set an access password" data-testid="admin-deck-password-input" />
              {editing.id && editing.has_password && (
                <button type="button" className="admin-clear-pw" onClick={() => set("password", "__CLEAR__")} data-testid="admin-deck-clear-password">Remove password protection</button>
              )}

              <label className="admin-survey-active-toggle">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="admin-deck-active-toggle" />
                <span>Active (visible in the public vault)</span>
              </label>

              <Button className="primary-cta full" onClick={save} disabled={busy} data-testid="admin-deck-save-button"><Save size={16} /> {busy ? "Saving…" : "Save deck"}</Button>
            </div>
          </div>
        </div>
      )}

      {logsFor && (
        <div className="survey-modal-overlay" onClick={() => setLogsFor(null)} data-testid="admin-deck-logs-overlay">
          <div className="survey-modal admin-survey-editor" onClick={(e) => e.stopPropagation()} data-testid="admin-deck-logs-modal">
            <div className="survey-modal-bar">
              <strong>{logsFor.title} · {logs.length} access logs</strong>
              <button onClick={() => setLogsFor(null)} data-testid="admin-deck-logs-close"><X size={18} /></button>
            </div>
            <div className="admin-survey-editor-body">
              {!logs.length && <p className="admin-hint" data-testid="admin-no-logs">No access yet.</p>}
              {logs.map((l, i) => (
                <article key={l.id} className="admin-response-card" data-testid={`admin-deck-log-${i}`}>
                  <span className="admin-response-date">{new Date(l.created_at).toLocaleString()} · {l.unlocked ? "Opened" : "Wrong password"}</span>
                  <div className="admin-response-answer"><strong>Name</strong><span>{l.name}</span></div>
                  <div className="admin-response-answer"><strong>Firm</strong><span>{l.firm}</span></div>
                  <div className="admin-response-answer"><strong>Mobile</strong><span>{l.mobile}</span></div>
                  <div className="admin-response-answer"><strong>Email</strong><span>{l.email || "—"}</span></div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
